import { useEffect, useState } from 'react';
import { Alert, Badge, Button, Dropdown, Form, Modal, Nav, Spinner, Table } from 'react-bootstrap';
import { usePagedList, Pager } from 'rentalui-finance';
import {
  listEmailLog, adminListEmailLog, listEmailSuppressions, adminListEmailSuppressions,
  addEmailSuppression, removeEmailSuppression, adminRemoveEmailSuppression,
  updateEmailSuppression, adminUpdateEmailSuppression,
  listEmailCategoryBlocks, addEmailCategoryBlock, updateEmailCategoryBlock, removeEmailCategoryBlock
} from '../api';

// email_log_and_suppression requirement - log of every email the platform has sent, plus
// per-owner suppression and admin-level category blocks. Scope 'admin' = website admin
// (platform-wide); scope 'owner' = their own log + their own "do not email" list.

const STATUS_VARIANT = { SENT: 'success', FAILED: 'danger', SUPPRESSED: 'secondary' };

// Kept in sync with rentalapi/common/emailPolicy.js
const NON_CRITICAL = [
  { value: 'BOOKING_REMINDER', label: 'Booking reminder', dir: 'TO_CUSTOMER' },
  { value: 'FOLLOW_UP', label: 'Follow-up', dir: 'TO_CUSTOMER' },
  { value: 'RIDE_UPDATE', label: 'Ride update', dir: 'TO_CUSTOMER' },
  { value: 'MEMBERSHIP_RENEWAL', label: 'Membership renewal reminder', dir: 'TO_CUSTOMER' },
  { value: 'SUBSCRIPTION_REMINDER', label: 'Subscription renewal reminder', dir: 'TO_OWNER' },
  { value: 'MEMBERSHIP_RENEWAL_DIGEST', label: 'Membership renewal digest', dir: 'TO_OWNER' },
  { value: 'FOLLOW_UP_STAFF', label: 'Staff follow-up ping', dir: 'TO_OWNER' },
  { value: 'LEAD_REMINDER', label: 'Lead follow-up reminder', dir: 'TO_ADMIN' },
  { value: 'GENERAL', label: 'General', dir: 'OTHER' },
];

function catLabel(category) {
  const found = NON_CRITICAL.find((c) => c.value === category);
  if (found) return found.label;
  return (category || 'GENERAL').replace(/_/g, ' ').toLowerCase().replace(/^./, (c) => c.toUpperCase());
}

function SuppressionsLabel({ categories }) {
  if (!categories || categories.length === 0) return <span className="text-muted small">All non-critical</span>;
  return <span className="small">{categories.map(catLabel).join(', ')}</span>;
}

// ---- Searchable customer picker -------------------------------------------------------------
// Queries the email log as the user types and presents matching recipients as a dropdown list.
// Used in BlockModal when no log row is pre-filled (manual "Add suppression" path).
function CustomerPicker({ searchFn, email, onEmail, name, onName }) {
  const [query, setQuery] = useState(email || '');
  const [results, setResults] = useState([]);
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    const q = query.trim();
    if (!q || q === email) { setResults([]); setOpen(false); return; }
    setLoading(true);
    const timer = setTimeout(async () => {
      try {
        const res = await searchFn(q);
        setResults(res);
        setOpen(res.length > 0);
      } catch { setResults([]); }
      finally { setLoading(false); }
    }, 300);
    return () => clearTimeout(timer);
  }, [query]); // eslint-disable-line react-hooks/exhaustive-deps

  const pick = (c) => {
    setQuery(c.email);
    onEmail(c.email);
    onName(c.name || '');
    setOpen(false);
    setResults([]);
  };

  return (
    <div className="position-relative">
      <div className="d-flex align-items-center gap-1">
        <Form.Control
          type="email" required placeholder="Search name or email…"
          value={query} autoComplete="off"
          onChange={(e) => { setQuery(e.target.value); onEmail(e.target.value); }}
          onBlur={() => setTimeout(() => setOpen(false), 150)}
        />
        {loading && <Spinner animation="border" size="sm" />}
      </div>
      {open && (
        <div className="border rounded bg-white shadow-sm position-absolute w-100"
          style={{ zIndex: 1060, maxHeight: 200, overflowY: 'auto' }}>
          {results.map((c, i) => (
            <button key={i} type="button" className="dropdown-item small py-1 px-3"
              onMouseDown={(e) => { e.preventDefault(); pick(c); }}>
              {c.email}
              {c.name && <span className="text-muted ms-1 small">({c.name})</span>}
            </button>
          ))}
        </div>
      )}
      <Form.Control className="mt-2" placeholder="Name (optional)" value={name}
        onChange={(e) => onName(e.target.value)} />
    </div>
  );
}

// ---- Block Modal ----------------------------------------------------------------------------
// logRow: pre-filled log entry (email + category). null = opened from "Add suppression" tab.
// searchFn: async (q: string) => [{email, name}] — used when logRow is null.
function BlockModal({ show, onHide, logRow, isAdmin, onDone, searchFn }) {
  const prefillEmail = logRow?.to || '';
  const prefillCategory = logRow?.category || '';
  const prefillBookingId = logRow?.bookingId || null;

  const [mode, setMode] = useState(
    prefillCategory && !isAdmin ? 'customer_category' : 'customer_all'
  );
  // When a booking is available, default to booking-scoped to avoid silencing other orders.
  const [bookingScope, setBookingScope] = useState(prefillBookingId ? 'booking' : 'customer');
  const [customEmail, setCustomEmail] = useState('');
  const [customerName, setCustomerName] = useState('');
  const [selectedCats, setSelectedCats] = useState(prefillCategory ? [prefillCategory] : []);
  const [temporary, setTemporary] = useState(false);
  const [expiresAt, setExpiresAt] = useState('');
  const [reason, setReason] = useState('');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  const reset = () => {
    setMode(prefillCategory && !isAdmin ? 'customer_category' : 'customer_all');
    setBookingScope(prefillBookingId ? 'booking' : 'customer');
    setCustomEmail('');
    setCustomerName('');
    setSelectedCats(prefillCategory ? [prefillCategory] : []);
    setTemporary(false);
    setExpiresAt('');
    setReason('');
    setError('');
  };

  const handleHide = () => { reset(); onHide(); };

  const toggleCat = (v) =>
    setSelectedCats((prev) => (prev.includes(v) ? prev.filter((c) => c !== v) : [...prev, v]));

  const submit = async (e) => {
    e.preventDefault();
    setSaving(true);
    setError('');
    try {
      const expiry = temporary && expiresAt ? expiresAt : null;
      if (mode === 'global_category') {
        if (!selectedCats.length) { setError('Select at least one category to block.'); setSaving(false); return; }
        for (const cat of selectedCats) {
          await addEmailCategoryBlock({ category: cat, reason, temporary, expiresAt: expiry });
        }
      } else {
        const addr = (logRow ? prefillEmail : customEmail).trim();
        if (!addr) { setError('Email address is required.'); setSaving(false); return; }
        await addEmailSuppression({
          customerEmail: addr,
          customerName: customerName || logRow?.customerName || '',
          reason,
          categories: mode === 'customer_category' ? selectedCats : [],
          temporary,
          expiresAt: expiry,
          bookingId: (bookingScope === 'booking' && prefillBookingId) ? prefillBookingId : null,
        });
      }
      handleHide();
      onDone();
    } catch (err) {
      setError(err.response?.data?.msg || 'Unable to save the block.');
    } finally {
      setSaving(false);
    }
  };

  const customerCats = NON_CRITICAL.filter((c) => c.dir === 'TO_CUSTOMER');
  const todayStr = new Date().toISOString().split('T')[0];

  return (
    <Modal show={show} onHide={handleHide}>
      <Form onSubmit={submit}>
        <Modal.Header closeButton>
          <Modal.Title>Block emails</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {error && <Alert variant="danger" className="py-2 small">{error}</Alert>}
          <p className="small text-muted mb-3">
            Booking confirmations, status changes and payment receipts always go through —
            only reminders and follow-ups can be blocked.
          </p>

          <Form.Group className="mb-3">
            <Form.Label className="fw-semibold">Block type</Form.Label>
            <div className="d-flex flex-column gap-1">
              <Form.Check type="radio" id="bm-all" label="Stop all non-critical emails to a customer"
                checked={mode === 'customer_all'} onChange={() => setMode('customer_all')} />
              <Form.Check type="radio" id="bm-cat" label="Stop only specific reminder types to a customer"
                checked={mode === 'customer_category'} onChange={() => setMode('customer_category')} />
              {isAdmin && (
                <Form.Check type="radio" id="bm-global" label="Block a reminder type platform-wide (all owners & customers)"
                  checked={mode === 'global_category'} onChange={() => setMode('global_category')} />
              )}
            </div>
          </Form.Group>

          {(mode === 'customer_all' || mode === 'customer_category') && (
            <Form.Group className="mb-3">
              <Form.Label>Customer</Form.Label>
              {logRow ? (
                <Form.Control type="email" value={prefillEmail} disabled />
              ) : (
                <CustomerPicker
                  searchFn={searchFn}
                  email={customEmail} onEmail={setCustomEmail}
                  name={customerName} onName={setCustomerName}
                />
              )}
            </Form.Group>
          )}

          {/* Booking scope — only relevant when the modal was opened from a log row that has a bookingId */}
          {(mode === 'customer_all' || mode === 'customer_category') && prefillBookingId && (
            <Form.Group className="mb-3">
              <Form.Label className="fw-semibold">Apply to</Form.Label>
              <div className="d-flex flex-column gap-1">
                <Form.Check type="radio" id="bs-booking"
                  label={`This booking only (order #${prefillBookingId})`}
                  checked={bookingScope === 'booking'}
                  onChange={() => setBookingScope('booking')} />
                <Form.Check type="radio" id="bs-customer"
                  label="All bookings for this customer"
                  checked={bookingScope === 'customer'}
                  onChange={() => setBookingScope('customer')} />
              </div>
              {bookingScope === 'customer' && (
                <div className="small text-warning mt-1">
                  This will stop reminders for all current and future bookings by this customer.
                </div>
              )}
            </Form.Group>
          )}

          {mode === 'customer_category' && (
            <Form.Group className="mb-3">
              <Form.Label>Which reminder types to block</Form.Label>
              <div className="d-flex flex-column gap-1 ps-1">
                {customerCats.map((c) => (
                  <Form.Check key={c.value} type="checkbox" id={`bc-${c.value}`} label={c.label}
                    checked={selectedCats.includes(c.value)} onChange={() => toggleCat(c.value)} />
                ))}
              </div>
            </Form.Group>
          )}

          {mode === 'global_category' && (
            <Form.Group className="mb-3">
              <Form.Label>Categories to block globally</Form.Label>
              <div className="d-flex flex-column gap-1 ps-1">
                {NON_CRITICAL.map((c) => (
                  <Form.Check key={c.value} type="checkbox" id={`bg-${c.value}`}
                    label={`${c.label} (${c.dir.replace('TO_', '').toLowerCase()})`}
                    checked={selectedCats.includes(c.value)} onChange={() => toggleCat(c.value)} />
                ))}
              </div>
            </Form.Group>
          )}

          <Form.Group className="mb-2">
            <Form.Label>Reason (optional)</Form.Label>
            <Form.Control as="textarea" rows={2} value={reason}
              onChange={(e) => setReason(e.target.value)}
              placeholder="e.g. Customer asked to stop reminders" />
          </Form.Group>

          <Form.Check id="bm-tmp" label="Temporary — set an expiry date"
            checked={temporary} onChange={(e) => setTemporary(e.target.checked)} className="mb-2" />
          {temporary && (
            <Form.Group>
              <Form.Label>Expires on</Form.Label>
              <Form.Control type="date" value={expiresAt} min={todayStr} required
                onChange={(e) => setExpiresAt(e.target.value)} />
            </Form.Group>
          )}
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={handleHide}>Cancel</Button>
          <Button type="submit" variant="danger" disabled={saving}>
            {saving ? 'Saving…' : 'Apply block'}
          </Button>
        </Modal.Footer>
      </Form>
    </Modal>
  );
}

// ---- Category Blocks tab (admin only) --------------------------------------------------------
function CategoryBlocksTab({ onError }) {
  const [filters, setFilters] = useState({ category: '', active: '' });
  const list = usePagedList(
    (params) => listEmailCategoryBlocks({ ...filters, ...params }),
    { deps: [filters.category, filters.active] }
  );
  const [showAdd, setShowAdd] = useState(false);
  const [addForm, setAddForm] = useState({ category: '', reason: '', temporary: false, expiresAt: '' });
  const [saving, setSaving] = useState(false);

  const toggleActive = async (block) => {
    try {
      await updateEmailCategoryBlock(block._id, { active: !block.active });
      list.reload();
    } catch (err) {
      onError(err.response?.data?.msg || 'Unable to update block.');
    }
  };

  const remove = async (id) => {
    try {
      await removeEmailCategoryBlock(id);
      list.reload();
    } catch (err) {
      onError(err.response?.data?.msg || 'Unable to remove block.');
    }
  };

  const submitAdd = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      await addEmailCategoryBlock({
        category: addForm.category,
        reason: addForm.reason,
        temporary: addForm.temporary,
        expiresAt: addForm.temporary && addForm.expiresAt ? addForm.expiresAt : null
      });
      setShowAdd(false);
      setAddForm({ category: '', reason: '', temporary: false, expiresAt: '' });
      list.reload();
    } catch (err) {
      onError(err.response?.data?.msg || 'Unable to create category block.');
    } finally {
      setSaving(false);
    }
  };

  const todayStr = new Date().toISOString().split('T')[0];

  return (
    <>
      <div className="d-flex flex-wrap align-items-center gap-2 mb-3">
        <Form.Select size="sm" style={{ maxWidth: 220 }} value={filters.category}
          onChange={(e) => setFilters((f) => ({ ...f, category: e.target.value }))}>
          <option value="">All categories</option>
          {NON_CRITICAL.map((c) => <option key={c.value} value={c.value}>{c.label}</option>)}
        </Form.Select>
        <Form.Select size="sm" style={{ maxWidth: 140 }} value={filters.active}
          onChange={(e) => setFilters((f) => ({ ...f, active: e.target.value }))}>
          <option value="">All</option>
          <option value="true">Active</option>
          <option value="false">Inactive</option>
        </Form.Select>
        <Button size="sm" variant="outline-danger" className="ms-auto" onClick={() => setShowAdd(true)}>
          Add category block
        </Button>
      </div>

      {list.loading ? <Spinner animation="border" size="sm" role="status" /> : list.rows.length === 0 ? (
        <Alert variant="light">No platform-wide category blocks.</Alert>
      ) : (
        <>
          <Table responsive hover size="sm">
            <thead>
              <tr>
                <th>Category</th>
                <th>Status</th>
                <th>Expires</th>
                <th>Reason</th>
                <th>Created by</th>
                <th />
              </tr>
            </thead>
            <tbody>
              {list.rows.map((b) => (
                <tr key={b._id} className={b.active ? '' : 'text-muted'}>
                  <td className="small fw-semibold">{catLabel(b.category)}</td>
                  <td>
                    <Badge bg={b.active ? 'danger' : 'secondary'}>{b.active ? 'Active' : 'Inactive'}</Badge>
                  </td>
                  <td className="small">
                    {b.expiresAt ? new Date(b.expiresAt).toLocaleDateString('en-IN') : '—'}
                  </td>
                  <td className="small">{b.reason || '—'}</td>
                  <td className="small">{b.createdBy?.name || b.createdBy?.email || '—'}</td>
                  <td className="d-flex gap-1">
                    <Button size="sm" variant={b.active ? 'outline-secondary' : 'outline-success'}
                      onClick={() => toggleActive(b)}>
                      {b.active ? 'Pause' : 'Resume'}
                    </Button>
                    <Button size="sm" variant="outline-danger" onClick={() => remove(b._id)}>
                      Delete
                    </Button>
                  </td>
                </tr>
              ))}
            </tbody>
          </Table>
          <Pager {...list} />
        </>
      )}

      <Modal show={showAdd} onHide={() => setShowAdd(false)}>
        <Form onSubmit={submitAdd}>
          <Modal.Header closeButton><Modal.Title>Add platform-wide category block</Modal.Title></Modal.Header>
          <Modal.Body>
            <p className="small text-muted">
              Stops that email category from sending to anyone on the platform. Critical emails
              are unaffected regardless.
            </p>
            <Form.Group className="mb-2">
              <Form.Label>Category</Form.Label>
              <Form.Select required value={addForm.category}
                onChange={(e) => setAddForm((f) => ({ ...f, category: e.target.value }))}>
                <option value="">Choose a category…</option>
                {NON_CRITICAL.map((c) => (
                  <option key={c.value} value={c.value}>{c.label} ({c.dir.replace('TO_', '').toLowerCase()})</option>
                ))}
              </Form.Select>
            </Form.Group>
            <Form.Group className="mb-2">
              <Form.Label>Reason</Form.Label>
              <Form.Control as="textarea" rows={2} value={addForm.reason}
                onChange={(e) => setAddForm((f) => ({ ...f, reason: e.target.value }))}
                placeholder="e.g. Temporary hold during data migration" />
            </Form.Group>
            <Form.Check id="cb-tmp" label="Temporary — set an expiry date" className="mb-2"
              checked={addForm.temporary} onChange={(e) => setAddForm((f) => ({ ...f, temporary: e.target.checked }))} />
            {addForm.temporary && (
              <Form.Group>
                <Form.Label>Expires on</Form.Label>
                <Form.Control type="date" value={addForm.expiresAt} min={todayStr} required
                  onChange={(e) => setAddForm((f) => ({ ...f, expiresAt: e.target.value }))} />
              </Form.Group>
            )}
          </Modal.Body>
          <Modal.Footer>
            <Button variant="secondary" onClick={() => setShowAdd(false)}>Cancel</Button>
            <Button type="submit" variant="danger" disabled={saving}>{saving ? 'Saving…' : 'Block category'}</Button>
          </Modal.Footer>
        </Form>
      </Modal>
    </>
  );
}

// ---- Main component --------------------------------------------------------------------------
export default function EmailsPanel({ scope = 'owner' }) {
  const isAdmin = scope === 'admin';
  const [view, setView] = useState('log');
  const [filters, setFilters] = useState({ status: '', q: '' });
  const [error, setError] = useState('');
  const [blockRow, setBlockRow] = useState(null);
  const [showBlock, setShowBlock] = useState(false);
  // Tracks rows stopped inline without a modal: { [rowId]: 'all' | categoryStr }
  const [stoppedRows, setStoppedRows] = useState({});

  const logList = usePagedList(
    (params) => (isAdmin ? adminListEmailLog : listEmailLog)({ ...filters, ...params }),
    { deps: [filters.status, filters.q], enabled: view === 'log' }
  );
  const suppList = usePagedList(
    (params) => (isAdmin ? adminListEmailSuppressions : listEmailSuppressions)({ q: filters.q, ...params }),
    { deps: [filters.q], enabled: view === 'suppressions' }
  );

  const openBlock = (row) => { setBlockRow(row); setShowBlock(true); };

  // Searches the email log for matching recipients — used by CustomerPicker in BlockModal.
  const searchCustomers = async (q) => {
    try {
      const res = await (isAdmin ? adminListEmailLog : listEmailLog)({ q, limit: 20 });
      const seen = new Set();
      const customers = [];
      for (const row of res.data || []) {
        if (row.to && !seen.has(row.to)) {
          seen.add(row.to);
          customers.push({ email: row.to, name: row.customerId?.name || '' });
        }
      }
      return customers;
    } catch { return []; }
  };

  // One-click stop from a log row — no modal. kind: 'all' or a specific category string.
  // Always scoped to the booking on the row so other bookings for the same customer are unaffected.
  const quickStop = async (row, kind) => {
    setError('');
    try {
      await addEmailSuppression({
        customerEmail: row.to,
        customerName: row.customerId?.name || '',
        categories: kind === 'all' ? [] : [kind],
        bookingId: row.bookingId || null,
      });
      setStoppedRows((prev) => ({ ...prev, [row._id]: kind }));
    } catch (err) {
      setError(err.response?.data?.msg || 'Unable to stop emails.');
    }
  };

  const toggleSuppression = async (supp) => {
    setError('');
    try {
      await (isAdmin ? adminUpdateEmailSuppression : updateEmailSuppression)(supp._id, { active: !supp.active });
      suppList.reload();
    } catch (err) {
      setError(err.response?.data?.msg || 'Unable to update.');
    }
  };

  const removeSuppression = async (id) => {
    setError('');
    try {
      await (isAdmin ? adminRemoveEmailSuppression : removeEmailSuppression)(id);
      suppList.reload();
    } catch (err) {
      setError(err.response?.data?.msg || 'Unable to remove that entry.');
    }
  };

  return (
    <div>
      <Nav variant="pills" activeKey={view} onSelect={(k) => setView(k || 'log')} className="mb-3">
        <Nav.Item><Nav.Link eventKey="log">Email log</Nav.Link></Nav.Item>
        <Nav.Item><Nav.Link eventKey="suppressions">Do-not-email list</Nav.Link></Nav.Item>
        {isAdmin && <Nav.Item><Nav.Link eventKey="category_blocks">Category blocks</Nav.Link></Nav.Item>}
      </Nav>
      {error && <Alert variant="danger" dismissible onClose={() => setError('')}>{error}</Alert>}

      {/* ---- Email log tab ---- */}
      {view === 'log' && (
        <>
          <div className="d-flex flex-wrap align-items-center gap-2 mb-3">
            <Form.Select size="sm" style={{ maxWidth: 160 }} value={filters.status}
              onChange={(e) => setFilters((f) => ({ ...f, status: e.target.value }))}>
              <option value="">All statuses</option>
              <option value="SENT">Sent</option>
              <option value="FAILED">Failed</option>
              <option value="SUPPRESSED">Suppressed</option>
            </Form.Select>
            <Form.Control size="sm" style={{ maxWidth: 240 }} placeholder="Search subject / recipient"
              value={filters.q} onChange={(e) => setFilters((f) => ({ ...f, q: e.target.value }))} />
          </div>
          {logList.loading ? <Spinner animation="border" size="sm" role="status" /> : logList.rows.length === 0 ? (
            <Alert variant="light">No emails logged yet.</Alert>
          ) : (
            <>
              <Table responsive hover size="sm">
                <thead>
                  <tr>
                    <th>When</th>
                    {isAdmin && <th>Vertical</th>}
                    {isAdmin && <th>Owner</th>}
                    <th>To</th>
                    <th>Subject</th>
                    <th>Category</th>
                    <th>Status</th>
                    <th>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {logList.rows.map((r) => (
                    <tr key={r._id}>
                      <td className="small">{new Date(r.createdAt).toLocaleString('en-IN')}</td>
                      {isAdmin && <td className="small">{r.vertical || '—'}</td>}
                      {isAdmin && <td className="small">{r.ownerId?.name || r.ownerId?.email || '—'}</td>}
                      <td className="small">{r.to}</td>
                      <td className="small" style={{ maxWidth: 260 }}>{r.subject || '—'}</td>
                      <td className="small">{catLabel(r.category)}</td>
                      <td>
                        <Badge bg={STATUS_VARIANT[r.status] || 'secondary'}>{r.status}</Badge>
                        {r.status === 'FAILED' && r.error && <div className="small text-danger">{r.error}</div>}
                      </td>
                      <td style={{ whiteSpace: 'nowrap' }}>
                        {stoppedRows[r._id] ? (
                          <span className="small text-success">
                            ✓ Stopped{stoppedRows[r._id] !== 'all'
                              ? ` (${catLabel(stoppedRows[r._id])})`
                              : ' (all reminders)'}
                          </span>
                        ) : r.status === 'SUPPRESSED' ? (
                          <Badge bg="secondary" className="small">Suppressed</Badge>
                        ) : (
                          <Dropdown align="end">
                            <Dropdown.Toggle size="sm" variant="outline-danger" id={`act-${r._id}`}>
                              Stop emails
                            </Dropdown.Toggle>
                            {/* strategy:fixed escapes the table's overflow:auto container so
                                the menu is never clipped by the responsive table wrapper */}
                            <Dropdown.Menu renderOnMount popperConfig={{ strategy: 'fixed' }}>
                              <Dropdown.Header className="small text-truncate" style={{ maxWidth: 240 }}>
                                {r.to}
                              </Dropdown.Header>
                              <Dropdown.Item onClick={() => quickStop(r, 'all')}>
                                Stop all reminders
                              </Dropdown.Item>
                              {r.category && NON_CRITICAL.some((c) => c.value === r.category) && (
                                <Dropdown.Item onClick={() => quickStop(r, r.category)}>
                                  Stop only "{catLabel(r.category)}"
                                </Dropdown.Item>
                              )}
                              <Dropdown.Divider />
                              <Dropdown.Item onClick={() => openBlock(r)}>
                                More options…
                              </Dropdown.Item>
                            </Dropdown.Menu>
                          </Dropdown>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </Table>
              <Pager {...logList} />
            </>
          )}
        </>
      )}

      {/* ---- Do-not-email list tab ---- */}
      {view === 'suppressions' && (
        <>
          <div className="d-flex flex-wrap align-items-center gap-2 mb-3">
            <Form.Control size="sm" style={{ maxWidth: 240 }} placeholder="Search customer"
              value={filters.q} onChange={(e) => setFilters((f) => ({ ...f, q: e.target.value }))} />
            {!isAdmin && (
              <Button size="sm" variant="outline-danger" className="ms-auto" onClick={() => openBlock(null)}>
                Stop emailing a customer
              </Button>
            )}
          </div>
          {suppList.loading ? <Spinner animation="border" size="sm" role="status" /> : suppList.rows.length === 0 ? (
            <Alert variant="light">
              {isAdmin ? 'No owners have muted a customer.' : "You haven't stopped emails to any customer yet."}
            </Alert>
          ) : (
            <>
              <Table responsive hover size="sm">
                <thead>
                  <tr>
                    <th>Since</th>
                    {isAdmin && <th>Owner</th>}
                    <th>Customer email</th>
                    <th>Blocked emails</th>
                    <th>Expires</th>
                    <th>Status</th>
                    <th />
                  </tr>
                </thead>
                <tbody>
                  {suppList.rows.map((r) => (
                    <tr key={r._id} className={r.active ? '' : 'text-muted'}>
                      <td className="small">{new Date(r.createdAt).toLocaleDateString('en-IN')}</td>
                      {isAdmin && <td className="small">{r.ownerId?.name || r.ownerId?.email || '—'}</td>}
                      <td className="small">
                        {r.customerEmail}
                        {r.customerName && <div className="text-muted">{r.customerName}</div>}
                        {r.bookingId
                          ? <div className="text-muted">Booking #{String(r.bookingId).slice(-6)}</div>
                          : <div className="text-muted fst-italic">All bookings</div>}
                        {r.reason && <div className="text-muted fst-italic">{r.reason}</div>}
                      </td>
                      <td><SuppressionsLabel categories={r.categories} /></td>
                      <td className="small">
                        {r.expiresAt ? new Date(r.expiresAt).toLocaleDateString('en-IN') : '—'}
                      </td>
                      <td>
                        <Badge bg={r.active ? 'danger' : 'secondary'}>{r.active ? 'Active' : 'Paused'}</Badge>
                      </td>
                      <td className="d-flex gap-1">
                        <Button size="sm" variant={r.active ? 'outline-secondary' : 'outline-success'}
                          onClick={() => toggleSuppression(r)}>
                          {r.active ? 'Pause' : 'Resume'}
                        </Button>
                        <Button size="sm" variant="outline-danger" onClick={() => removeSuppression(r._id)}>
                          {isAdmin ? 'Remove' : 'Delete'}
                        </Button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </Table>
              <Pager {...suppList} />
            </>
          )}
        </>
      )}

      {/* ---- Category blocks tab (admin only) ---- */}
      {view === 'category_blocks' && isAdmin && (
        <CategoryBlocksTab onError={setError} />
      )}

      <BlockModal
        show={showBlock}
        onHide={() => setShowBlock(false)}
        logRow={blockRow}
        isAdmin={isAdmin}
        searchFn={searchCustomers}
        onDone={() => {
          suppList.reload();
          setView('suppressions');
        }}
      />
    </div>
  );
}
